import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, filter, finalize, firstValueFrom, map, tap } from 'rxjs';
import { MessageGroup } from './messageGroup.model';
import { FirebaseApiProvider } from 'libs/api/providers/firebase-api.provider';
import { Message } from './message.model';
import { ReactionKey } from './message-reactions';
import { UnsubscriberService } from 'libs/unsubcriber/unsubscriber.service';

import { environment } from 'apps/craf2s-client-app/src/environments/environment';
import { User } from 'libs/user/user.model';
import { Storage, deleteObject, ref } from '@angular/fire/storage';
import { WhereQuery } from 'libs/api/api.provider';

@Injectable({
  providedIn: 'root',
})
export class MessageService {
  userMessageGroupsRole: string;
  private isFetchingMessageGroups = false;
  private messageGroupsSubject: BehaviorSubject<MessageGroup[]> = new BehaviorSubject([]);
  messageGroups$: Observable<MessageGroup[]> = this.messageGroupsSubject.asObservable();

  private messagesSubject: {
    [messengerId: string]: BehaviorSubject<Message[]>[];
  } = {};
  messages$: {
    [messengerId: string]: Observable<Message[]>[];
  } = {};

  private allMessagesSubject: {
    [messengerId: string]: ReplaySubject<Message[]>;
  } = {};
  allMessages$: {
    [messengerId: string]: Observable<Message[]>;
  } = {};

  constructor(
    private apiService: FirebaseApiProvider,
    // CAN'T USE UPLOAD SERVICE ON ADMIN (NEED CORDOVA)
    // USE Fb Storage directly to delete medias (to refactor)
    private storage: Storage,
    private unsubscriberService: UnsubscriberService,
  ) {}

  getUserMessageGroups(role: string): Observable<MessageGroup[]> {
    if (this.userMessageGroupsRole !== role) {
      this.userMessageGroupsRole = role;
      this.isFetchingMessageGroups = true;

      this.apiService
        .fetchAll<MessageGroup>('messageGroups', MessageGroup.fromObject, [
          {
            fieldPath: 'roles',
            condition: 'array-contains',
            value: role,
          },
        ])
        .then((messageGroups) => {
          console.log('getUserMessageGroups API FETCH:', role, messageGroups);
          this.isFetchingMessageGroups = false;
          this.messageGroupsSubject.next(messageGroups);
        });
    } else {
      console.log('getUserMessageGroups FROM CACHE:', role);
    }

    // Avant : `filter(messageGroups.length > 0)` — bloquait indéfiniment tout
    // rôle dont le résultat réel est une liste vide (ex. identité alumni dont
    // le centre n'a personne au champ `role` legataire 'alumni'/'manager' —
    // voir UserService.getAlumni ci-dessous, même défaut). L'écran concerné
    // (`*ngIf="data$ | async"`, aucun état de secours) restait blanc pour
    // toujours. On ne filtre plus que l'attente de LA RÉPONSE EN COURS
    // (`isFetchingMessageGroups`), jamais le contenu de cette réponse — une
    // liste vide authentique est désormais délivrée normalement.
    return this.messageGroups$.pipe(filter(() => !this.isFetchingMessageGroups));
  }

  /** S6-03 — tous les groupes, sans filtre de rôle : résolution de titre pour l'export, pas pour un fil d'un rôle donné. */
  async fetchAllMessageGroups(): Promise<MessageGroup[]> {
    return this.apiService.fetchAll<MessageGroup>('messageGroups', MessageGroup.fromObject);
  }

  listenToMessages(
    trainingCenterId: string,
    trainingId: string,
    messengerId: string,
    messageGroup: MessageGroup,
    toUser: User,
    fromUser: User,
    startAfterValue?: any,
  ) {
    console.log('listenToMessages API CALL LISTENING:', trainingId, messengerId);

    if (!this.allMessagesSubject[messengerId]) {
      // Do not emit an artificial empty list before Firestore has returned its
      // first snapshot. The messenger uses the first emission to reveal the
      // list and scroll to the bottom; emitting [] here made that scroll happen
      // before the real messages existed.
      this.allMessagesSubject[messengerId] = new ReplaySubject<Message[]>(1);
      this.allMessages$[messengerId] = this.allMessagesSubject[messengerId].asObservable();
    }

    if (!this.messagesSubject[messengerId]) {
      this.messagesSubject[messengerId] = [];
      this.messages$[messengerId] = [];
    }

    this.messagesSubject[messengerId].push(new BehaviorSubject<Message[]>([]));

    const listenerIndex = this.messagesSubject[messengerId].length - 1;

    this.messages$[messengerId].push(this.messagesSubject[messengerId][listenerIndex].asObservable());

    const whereQ: WhereQuery[] = [];
    if (trainingCenterId) {
      whereQ.push({
        fieldPath: 'trainingCenterId',
        condition: '==',
        value: trainingCenterId,
      });
    }
    if (trainingId) {
      whereQ.push({
        fieldPath: 'trainingId',
        condition: '==',
        value: trainingId,
      });
    }

    // Lot 1D (audit 28/10/2026, F-06) — indispensable à la règle de lecture :
    // Firestore ne peut autoriser une requête de LISTE que si elle est
    // « prouvable » depuis ses PROPRES clauses where (pas depuis une
    // évaluation après coup des documents retournés) — c'est la cause réelle,
    // enfin identifiée, du blocage d'export rencontré en diagnostic le 28
    // octobre. toUser est le viewer courant (voir l'appel dans
    // MessengerComponent.getMessages).
    whereQ.push({
      fieldPath: 'participantIds',
      condition: 'array-contains',
      value: toUser.id,
    });
    // Vu en direct sur l'émulateur : une fois la requête "prouvable" par AU
    // MOINS une contrainte (participantIds ci-dessus), Firestore n'applique
    // PAS le reste de la règle en filtrage après coup — un champ qui n'est
    // dans AUCUNE clause where du tout peut se retrouver ignoré plutôt que
    // vérifié. isArchivedTraining doit donc, lui aussi, être une contrainte
    // explicite de la requête pour être réellement appliqué par les règles.
    whereQ.push({
      fieldPath: 'isArchivedTraining',
      condition: '==',
      value: false,
    });

    if (messageGroup) {
      whereQ.push({
        fieldPath: 'messageGroupId',
        condition: '==',
        value: messengerId,
      });
    } else {
      whereQ.push({
        fieldPath: 'directUserIds',
        condition: 'in',
        value: [
          [toUser.id, fromUser.id],
          [fromUser.id, toUser.id],
        ],
      });
    }

    const messagesSubscriber = this.apiService
      .listenToChanges<Message>(
        'messages',
        Message.fromObject,
        whereQ,
        'date',
        'desc',
        environment.limitMessagesCount,
        startAfterValue,
      )
      .pipe(
        finalize(() => {
          delete this.messagesSubject[messengerId];
          delete this.messages$[messengerId];
          delete this.allMessagesSubject[messengerId];
          delete this.allMessages$[messengerId];
          console.log(`listenToMessages ${messengerId} ${listenerIndex} finalized.`);
        }),
      )
      .subscribe((messages) => {
        const withAuthorMessages = messages.map((message) => {
          message.author = messageGroup?.users?.find((user) => user.id === message.authorId) ?? fromUser;
          return message;
        });

        console.log(`listenToMessages ${messengerId} ${listenerIndex} NEXT:`, withAuthorMessages);
        this.messagesSubject[messengerId][listenerIndex].next(withAuthorMessages);
        this.allMessagesSubject[messengerId].next(
          this.messagesSubject[messengerId].reduce(
            (allMessages, subject) => [...allMessages, ...subject.getValue()],
            [],
          ),
        );
      });

    this.unsubscriberService.add(`MessageService:listenToMessages:${messengerId}`, messagesSubscriber);

    return this.allMessages$[messengerId];
  }

  async sendNewMessage(message: Message): Promise<Message> {
    try {
      const data = Message.toObject(message);
      message.id = await this.apiService.create<Message>('messages', data, Message.toObject);
      return message;
    } catch (e) {
      throw e;
    }
  }

  async deleteMessage(messageId: string) {
    try {
      // console.log('deleteMessage messageId:', messageId);
      await this.apiService.delete('messages', messageId);
    } catch (e) {
      throw e;
    }
  }

  async setMessagesReadBy(messages: Message[], userId: string): Promise<void> {
    try {
      const ids = messages.filter((message) => !message.readBy?.includes(userId)).map((message) => message.id);

      // Was updateBatch, which writes with set() and replaces the whole document:
      // two readers at once dropped each other's readBy, and any field outside
      // Message.toObject was destroyed. Touching only readBy fixes both.
      await this.apiService.updateArrayField('messages', ids, 'readBy', userId, 'add');

      messages.forEach((message) => {
        if (!message.readBy) {
          message.readBy = [];
        }
        if (!message.readBy.includes(userId)) {
          message.readBy.push(userId);
        }
      });
    } catch (e) {
      throw e;
    }
  }

  /**
   * Writes one reaction, add or remove, as told.
   *
   * Deliberately does not re-read the message to decide: the caller applies an
   * optimistic update to the bubble first, so anything read here would already
   * reflect the change and invert the operation. The decision belongs to
   * nextReactionOperation, evaluated once on the pre-update state.
   */
  async setMessageReaction(
    messageId: string,
    key: ReactionKey,
    userId: string,
    operation: 'add' | 'remove',
  ): Promise<void> {
    await this.apiService.updateArrayField('messages', [messageId], `reactions.${key}`, userId, operation);
  }

  /**
   * S6-03 — tout l'historique autorisé d'une formation, pas seulement la
   * fenêtre que charge le messenger (`limitMessagesCount`). Pagine par
   * lots de 1000, triés par date croissante.
   *
   * Limite connue et acceptée : la pagination utilise `date` comme curseur
   * (`startAfter`), pas l'id du document — en théorie, deux messages
   * partageant exactement la même date à la milliseconde, pile à la
   * frontière d'une page, pourraient être omis. Un lot de 1000 couvre déjà
   * la quasi-totalité des formations réelles en une seule page ; le risque
   * ne concerne qu'un volume largement supérieur ET une collision de date
   * exacte à la frontière — en pratique quasi inexistant pour des messages
   * humains (il faudrait resserrer `_buildQuery`, partagé par tout
   * l'appel à `fetchAll`/`listenToChanges` dans l'app, pour l'éliminer
   * complètement — hors de portée de ce ticket).
   */
  async fetchAllTrainingMessages(trainingId: string, trainingCenterId: string): Promise<Message[]> {
    if (!trainingId || !trainingCenterId) {
      throw new Error('Formation et centre requis pour charger les messages.');
    }
    const pageSize = 1000;
    const allMessages: Message[] = [];
    let startAfterValue: string | undefined;

    for (;;) {
      const page = await this.apiService.fetchAll<Message>(
        'messages',
        Message.fromObject,
        [
          {
            fieldPath: 'trainingId',
            condition: '==',
            value: trainingId,
          },
          // Lot 1D (audit 28/10/2026, F-06) — même raison que listenToMessages
          // ci-dessus : sans cette contrainte dans la requête elle-même,
          // Firestore ne peut pas prouver que isAdminOfCenterClaim() tient
          // pour tous les documents retournés, et refuse la LISTE entière.
          {
            fieldPath: 'trainingCenterId',
            condition: '==',
            value: trainingCenterId,
          },
        ],
        'date',
        'asc',
        pageSize,
        startAfterValue,
      );

      allMessages.push(...page);

      if (page.length < pageSize) {
        break;
      }
      startAfterValue = page[page.length - 1].date;
    }

    return allMessages;
  }

  async deleteAllTrainingMessages(trainingId: string, trainingCenterId: string) {
    await this.deleteMessages(await this.fetchAllTrainingMessages(trainingId, trainingCenterId), true);
  }

  async deleteTrainingUserMessages(userId: string, trainingId: string, trainingCenterId: string) {
    // Même requête bornée au centre que l'export : une LISTE doit permettre
    // aux règles de prouver le périmètre admin pour tous les résultats.
    const messages = await this.fetchAllTrainingMessages(trainingId, trainingCenterId);
    await this.deleteMessages(messages.filter((m) => m.authorId === userId || m.toUserId === userId), true);
  }

  async deleteTutorApprenticeMessages(
    tutorId: string,
    apprenticeId: string,
    trainingId: string,
    noOtherApprentices: boolean,
    trainingCenterId: string,
  ): Promise<void> {
    const groups = await firstValueFrom(this.getUserMessageGroups('tutor'));
    const tripartiteGroup = groups.find(
      (group) => JSON.stringify(group.singleRoles) === JSON.stringify(['apprentice', 'tutor']),
    );
    const tripartiteId = tripartiteGroup ? `${tripartiteGroup.id}/${apprenticeId}/${tutorId}` : null;
    const pair = new Set([tutorId, apprenticeId]);
    const messages = await this.fetchAllTrainingMessages(trainingId, trainingCenterId);
    const selected = messages.filter((m) =>
      (tripartiteId !== null && m.messageGroupId === tripartiteId) ||
      (pair.has(m.authorId) && pair.has(m.toUserId)) ||
      (noOtherApprentices && m.messageGroupId != null && m.authorId === tutorId),
    );
    await this.deleteMessages(selected, true);
  }

  private async deleteMessages(messages: Message[], withMedia = false) {
    try {
      if (withMedia === true) {
        const messagesWithMedia = messages.filter((message) => message.media !== undefined);
        await Promise.all(
          messagesWithMedia.map(async (message) => {
            console.log('Delete media:', message.media);
            const fileRef = ref(this.storage, message.media);
            try {
              await deleteObject(fileRef);
            } catch (e) {
              console.log(e);
            }
          }),
        );
      }
      const ids = Array.from(new Set(messages.map((n) => n.id)));
      console.log('Delete Messages: ', ids);
      for (let start = 0; start < ids.length; start += 400) {
        await this.apiService.deleteBatch('messages', ids.slice(start, start + 400));
      }
    } catch (e) {
      throw e;
    }
  }
}
